import { useEffect, useRef, useState } from "react";
import { api, formatApiError, resolveImage } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, UploadCloud, Loader2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = {
  kind: "photo",
  category: "carpentry",
  title_en: "",
  title_es: "",
  image_url: "",
  youtube_id: "",
  order: 0,
};

export default function AdminMedia() {
  const { t, pick } = useLang();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("photo");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [bulkCategory, setBulkCategory] = useState("carpentry");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const bulkInputRef = useRef(null);

  const load = () =>
    api.get("/media", { params: { kind: tab } }).then(({ data }) => setItems(data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onBulkFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBulkUploading(true);
    setBulkProgress({ done: 0, total: files.length });
    try {
      // Upload in chunks of 8 to avoid timeouts
      const CHUNK = 8;
      let totalCreated = 0;
      let totalErrors = [];
      for (let i = 0; i < files.length; i += CHUNK) {
        const batch = files.slice(i, i + CHUNK);
        const fd = new FormData();
        batch.forEach((f) => fd.append("files", f));
        const { data } = await api.post(
          `/admin/media-bulk?category=${encodeURIComponent(bulkCategory)}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        totalCreated += (data.created || []).length;
        totalErrors = totalErrors.concat(data.errors || []);
        setBulkProgress({ done: Math.min(i + batch.length, files.length), total: files.length });
      }
      if (totalCreated > 0) {
        toast.success(`Uploaded ${totalCreated} photo${totalCreated === 1 ? "" : "s"}`);
      }
      if (totalErrors.length > 0) {
        toast.error(`${totalErrors.length} file(s) failed`);
      }
      setTab("photo");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBulkUploading(false);
      setBulkProgress({ done: 0, total: 0 });
      if (bulkInputRef.current) bulkInputRef.current.value = "";
    }
  };

  const startNew = () => {
    setForm({ ...EMPTY, kind: tab });
    setEditing("new");
  };

  const startEdit = (m) => {
    setForm({ ...EMPTY, ...m });
    setEditing(m.id);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") {
        await api.post("/admin/media", payload);
        toast.success("Added");
      } else {
        await api.put(`/admin/media/${editing}`, payload);
        toast.success("Updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-media">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl sm:text-4xl text-ink">{t("admin.media")}</h1>
        <button
          data-testid="admin-new-media"
          onClick={startNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand text-ink-inverse text-sm hover:bg-brand-hover transition-colors"
        >
          <Plus size={16} /> Add {tab === "photo" ? "photo" : "video"}
        </button>
      </div>

      {/* BULK UPLOAD STRIP — photos only */}
      {tab === "photo" && (
        <div
          data-testid="bulk-upload-strip"
          className="bg-bg-surface border border-edge p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <div className="flex-1">
            <p className="text-eyebrow text-brand mb-1">Bulk upload</p>
            <p className="text-ink-muted text-xs sm:text-sm">
              Select up to 60 photos at once. They&apos;ll be added to the album below — you can re-tag each one later.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              data-testid="bulk-category"
              value={bulkCategory}
              onChange={(e) => setBulkCategory(e.target.value)}
              disabled={bulkUploading}
              className="bg-bg border border-edge px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-brand"
            >
              <option value="carpentry">Carpentry</option>
              <option value="construction">Construction</option>
              <option value="consultation">3D / Design</option>
            </select>
            <label
              data-testid="bulk-upload-button"
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm cursor-pointer transition-colors ${
                bulkUploading
                  ? "bg-bg-hover text-ink-muted cursor-wait"
                  : "bg-brand text-ink-inverse hover:bg-brand-hover"
              }`}
            >
              {bulkUploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {bulkProgress.total > 0
                    ? `Uploading ${bulkProgress.done}/${bulkProgress.total}…`
                    : "Uploading…"}
                </>
              ) : (
                <>
                  <UploadCloud size={16} /> Select photos
                </>
              )}
              <input
                ref={bulkInputRef}
                data-testid="bulk-upload-input"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif,image/heic"
                className="hidden"
                onChange={onBulkFiles}
                disabled={bulkUploading}
              />
            </label>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-8">
        {[
          { k: "photo", label: "Photos" },
          { k: "video", label: "Videos" },
        ].map((it) => (
          <button
            key={it.k}
            onClick={() => setTab(it.k)}
            data-testid={`media-tab-${it.k}`}
            className={`px-5 py-2 text-eyebrow transition-colors ${
              tab === it.k ? "text-ink-inverse bg-brand" : "text-ink-muted hover:text-brand"
            }`}
          >
            {it.label}
          </button>
        ))}
      </div>

      {editing && (
        <form
          onSubmit={save}
          data-testid="admin-media-form"
          className="bg-bg-surface border border-edge p-6 mb-10 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <Field label="Title (EN)" testId="media-title-en" value={form.title_en} onChange={(v) => setForm({ ...form, title_en: v })} />
          <Field label="Title (ES)" testId="media-title-es" value={form.title_es} onChange={(v) => setForm({ ...form, title_es: v })} />
          <div>
            <label className="text-eyebrow text-ink-muted block mb-2">Category</label>
            <select
              data-testid="media-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
            >
              <option value="carpentry">Carpentry</option>
              <option value="construction">Construction</option>
              <option value="consultation">3D / Design</option>
            </select>
          </div>
          <Field label="Order" type="number" testId="media-order" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
          {form.kind === "photo" ? (
            <div className="md:col-span-2">
              <ImageUpload
                label="Image"
                value={form.image_url}
                onChange={(v) => setForm({ ...form, image_url: v })}
                testId="media-image"
                required
              />
            </div>
          ) : (
            <>
              <Field
                label="YouTube video ID"
                testId="media-youtube-id"
                value={form.youtube_id}
                onChange={(v) => setForm({ ...form, youtube_id: v })}
                placeholder="dQw4w9WgXcQ"
                required
              />
              <div>
                <ImageUpload
                  label="Custom thumbnail (optional)"
                  value={form.image_url}
                  onChange={(v) => setForm({ ...form, image_url: v })}
                  testId="media-thumb"
                />
              </div>
            </>
          )}
          <div className="md:col-span-2 flex gap-3 pt-2">
            <button type="submit" data-testid="media-save" className="px-6 py-3 bg-brand text-ink-inverse text-sm hover:bg-brand-hover">
              {t("admin.save")}
            </button>
            <button type="button" onClick={() => setEditing(null)} className="px-6 py-3 border border-edge text-ink hover:border-brand text-sm">
              {t("admin.cancel")}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((m) => (
          <div key={m.id} className="bg-bg-surface border border-edge group">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={
                  m.image_url
                    ? resolveImage(m.image_url)
                    : m.youtube_id
                    ? `https://i.ytimg.com/vi/${m.youtube_id}/hqdefault.jpg`
                    : ""
                }
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <p className="text-eyebrow text-brand mb-1">{m.category}</p>
              <p className="text-ink text-sm truncate">{pick(m, "title") || "Untitled"}</p>
              <div className="flex gap-3 mt-3">
                <button onClick={() => startEdit(m)} className="text-ink-muted hover:text-brand" data-testid={`media-edit-${m.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(m.id)} className="text-ink-muted hover:text-bad" data-testid={`media-delete-${m.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-muted col-span-full text-center py-12">No items yet.</p>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId, full, placeholder }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-eyebrow text-ink-muted block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        placeholder={placeholder || ""}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
      />
    </div>
  );
}
